/**
 * Season Utilities for SG7 Frontend
 * 
 * Handles different season calendars:
 * - European leagues: August-July (season = year of August start)
 * - South American leagues: January-December (season = calendar year)
 * 
 * API-Football uses these conventions:
 * - Premier League 2025/26 → season: 2025
 * - Brasileirão 2026 → season: 2026 (calendar year)
 */

// Leagues that follow calendar year (Jan-Dec) instead of European season (Aug-Jul)
const CALENDAR_YEAR_LEAGUES = [
  1,    // FIFA World Cup (e.g. 2026)
  71,   // Brasileirão Série A
  72,   // Brasileirão Série B
  73,   // Copa do Brasil
  13,   // Copa Libertadores
  11,   // Copa Sudamericana
  128,  // Argentina Primera División
  129,  // Argentina Copa de la Liga
  32,   // World Cup Qualification (Europe) — uses cycle start year
];

/**
 * Gets the current season for a specific league
 * 
 * @param leagueApiId - The API-Football league ID
 * @returns The season year appropriate for that league
 * 
 * Examples (in January 2026):
 * - Premier League (39) → 2025 (European: Aug 2025 - Jul 2026)
 * - Brasileirão (71) → 2026 (Calendar year)
 */
export function getSeasonForLeague(leagueApiId: number): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-12
  
  // Calendar year leagues use current year
  if (CALENDAR_YEAR_LEAGUES.includes(leagueApiId)) {
    return year;
  }
  
  // European leagues: Aug-Jul cycle
  return month >= 8 ? year : year - 1;
}

/**
 * Gets the default European season (for when league is unknown)
 * 
 * @returns The current European season year
 */
export function getCurrentSeason(): number {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  
  return month >= 8 ? year : year - 1;
}

/**
 * Checks if a league uses calendar year seasons
 * 
 * @param leagueApiId - The API-Football league ID
 * @returns true if league uses calendar year
 */
export function isCalendarYearLeague(leagueApiId: number): boolean {
  return CALENDAR_YEAR_LEAGUES.includes(leagueApiId);
}

/**
 * Validates if a season year is reasonable
 */
export function isValidSeason(season: number): boolean {
  const currentYear = new Date().getFullYear();
  return season >= 2020 && season <= currentYear + 1;
}

/**
 * Gets season with fallback to current if invalid
 */
export function getSeasonOrDefault(season?: number, leagueApiId?: number): number {
  if (season && isValidSeason(season)) {
    return season;
  }
  return leagueApiId ? getSeasonForLeague(leagueApiId) : getCurrentSeason();
}
